/**
 * EquipmentTypeUnitsTab — STORY-1306 Phase 2 slice B (proto #13 units-tab).
 *
 * Read-view: a típushoz tartozó EquipmentUnit-ok listája (mockup: „Egységek
 * listája" tab). Sor-link a meglévő unit-editorra (`/equipment/:id`, #351–#356).
 *
 * DIVERGENCIA-NAPLÓ: a mockup „Új egység" CTA-ja NINCS — unit-CREATE flow még
 * nem létezik FE-n (a unit-editor meglévő egységet szerkeszt); külön slice, ha
 * story lesz belőle. A mockup lapozása (5/47) szintén nincs: a BE-lista nem
 * lapozott (newest first, teljes lista) — per-type egység-szám tipikusan kicsi.
 */

import { useEquipmentUnitsByType } from '@/hooks/use-equipment-units-by-type';
import { UnitStatusBadge } from '@/pages/equipment/unit-status-badge';
import { useTranslation } from '@kgc/i18n';
import { Link } from 'react-router-dom';

interface EquipmentTypeUnitsTabProps {
  typeId: string;
}

export function EquipmentTypeUnitsTab({ typeId }: EquipmentTypeUnitsTabProps) {
  const { t } = useTranslation();
  const { units, isLoading, isError } = useEquipmentUnitsByType(typeId);

  if (isLoading) {
    return (
      <p data-testid="units-tab-loading" className="py-6 text-sm text-muted-foreground">
        {t('equipment.types.editor.units.loading')}
      </p>
    );
  }

  if (isError) {
    return (
      <p data-testid="units-tab-error" className="py-6 text-sm text-rose-600 dark:text-rose-400">
        {t('equipment.types.editor.units.error')}
      </p>
    );
  }

  if (units.length === 0) {
    return (
      <p data-testid="units-tab-empty" className="py-6 text-sm text-muted-foreground">
        {t('equipment.types.editor.units.empty')}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        <span data-testid="units-tab-count">{units.length}</span>{' '}
        {t('equipment.types.editor.units.countSuffix')}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <th className="py-2 pr-4">{t('equipment.types.editor.units.serialNumber')}</th>
              <th className="py-2 pr-4">{t('equipment.types.editor.units.assetTag')}</th>
              <th className="py-2 pr-4">{t('equipment.types.editor.units.status')}</th>
              <th className="py-2 pr-4">{t('equipment.types.editor.units.createdAt')}</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {units.map(unit => (
              <tr
                key={unit.id}
                className="border-b border-gray-100 dark:border-gray-800"
                data-testid={`units-tab-row-${unit.id}`}
              >
                <td className="py-2 pr-4 font-medium text-foreground">{unit.serialNumber}</td>
                <td className="py-2 pr-4 text-muted-foreground">{unit.assetTag ?? '—'}</td>
                <td className="py-2 pr-4">
                  <UnitStatusBadge status={unit.status} />
                </td>
                <td className="py-2 pr-4 text-muted-foreground">
                  {unit.createdAt.toLocaleDateString('hu-HU')}
                </td>
                <td className="py-2 text-right">
                  <Link
                    to={`/equipment/${unit.id}`}
                    className="text-xs font-semibold text-primary hover:underline"
                    data-testid={`units-tab-open-${unit.id}`}
                  >
                    {t('equipment.types.editor.units.open')}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
